import React from "react";
import {
  Grid,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CardHeader,
  Box,
  Typography,
  Card,
  CardContent,
  FormHelperText,
  Autocomplete,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";

// Metro/Province list: Metro Manila first, then alphabetically
const METRO_PROVINCES = [
  "Metro Manila",
  "Abra",
  "Agusan del Norte",
  "Agusan del Sur",
  "Aklan",
  "Albay",
  "Antique",
  "Apayao",
  "Aurora",
  "Bataan",
  "Batanes",
  "Batangas",
  "Basilan",
  "Benguet",
  "Biliran",
  "Bohol",
  "Bukidnon",
  "Bulacan",
  "Cagayan",
  "Camarines Norte",
  "Camarines Sur",
  "Capiz",
  "Catanduanes",
  "Cavite",
  "Cebu",
  "Cotabato",
  "Davao de Oro",
  "Davao del Norte",
  "Davao del Sur",
  "Davao Occidental",
  "Davao Oriental",
  "Dinagat Islands",
  "Eastern Samar",
  "Guimaras",
  "Ifugao",
  "Ilocos Norte",
  "Ilocos Sur",
  "Iloilo",
  "Isabela",
  "Kalinga",
  "La Union",
  "Laguna",
  "Lanao del Norte",
  "Lanao del Sur",
  "Leyte",
  "Maguindanao del Norte",
  "Maguindanao del Sur",
  "Marinduque",
  "Masbate",
  "Misamis Occidental",
  "Misamis Oriental",
  "Mountain Province",
  "Negros Occidental",
  "Negros Oriental",
  "Northern Samar",
  "Nueva Ecija",
  "Nueva Vizcaya",
  "Occidental Mindoro",
  "Oriental Mindoro",
  "Palawan",
  "Pampanga",
  "Pangasinan",
  "Quezon",
  "Quirino",
  "Romblon",
  "Samar",
  "Sarangani",
  "Siquijor",
  "Sorsogon",
  "Southern Leyte",
  "Sulu",
  "Surigao del Norte",
  "Surigao del Sur",
  "Tarlac",
  "Tawi-Tawi",
  "Zambales",
  "Zamboanga del Norte",
  "Zamboanga del Sur",
  "Zamboanga Sibugay",
];

const ClientAddress = ({
  clientAddresses,
  nationalAddresses,
  handleAddressChange,
  handleAddressCheckboxChange,
  handleAddAddress,
  handleRemoveAddress,
  loading,
  errors,
  isEdit,
}) => (
  <>
    <Grid item xs={12}>
      <Grid container paddingBottom={2}>
        <CardHeader
          title={
            <Box display="flex" alignItems="center" width="100%" flexWrap="wrap">
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm="auto">
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", textTransform: "uppercase", fontSize: "1rem" }}
                    mr={2}
                  >
                    Job Site Address
                  </Typography>
                </Grid>
                <Grid item xs={12} sm="auto">
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddAddress}
                    size="small"
                    disabled={!isEdit || loading}
                    sx={{ fontSize: "0.875rem" }}
                  >
                    Add Another Address
                  </Button>
                </Grid>
              </Grid>
            </Box>
          }
          sx={{ padding: "16px" }}
        />
      </Grid>

      <Grid container spacing={2}>
        {clientAddresses.map((clientAddress, index) => (
          <Grid item xs={12} key={index}>
            <Card sx={{ marginBottom: "16px" }} variant="outlined">
              <CardContent sx={{ padding: "16px" }}>
                <Grid container spacing={2}>
                  {/* House/Unit Street */}
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="House/Unit Street"
                      name={`house_unit_street ${index + 1}`}
                      value={clientAddress.house_unit_street}
                      onChange={e => handleAddressChange(index, "house_unit_street", e.target.value)}
                      fullWidth
                      required
                      size="small"
                      disabled={!isEdit || loading}
                      error={!!errors[`addresses[${index}].house_unit_street`]}
                      helperText={errors[`addresses[${index}].house_unit_street`]}
                    />
                  </Grid>

                  {/* Metro/Province */}
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small" error={!!errors[`addresses[${index}].metro_province`]}>
                      <InputLabel id={`metro-label-${index}`}>Metro/Province</InputLabel>
                      <Select
                        labelId={`metro-label-${index}`}
                        label="Metro/Province"
                        name={`metro_province ${index + 1}`}
                        value={clientAddress.metro_province || ""}
                        onChange={e => handleAddressChange(index, "metro_province", e.target.value)}
                        disabled={!isEdit || loading}
                        size="small"
                      >
                        <MenuItem value="" disabled>Select Metro/Province</MenuItem>
                        {METRO_PROVINCES.map(p => (
                          <MenuItem key={p} value={p}>{p}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors[`addresses[${index}].metro_province`]}</FormHelperText>
                    </FormControl>
                  </Grid>

                  {/* City */}
                  <Grid item xs={12} md={2}>
                    <Autocomplete
                      freeSolo
                      disableClearable
                      options={[
                        ...new Set(
                          nationalAddresses
                            .filter(d => d.metro_province === clientAddress.metro_province)
                            .map(d => d.city)
                        )
                      ].sort()}
                      value={clientAddress.city || ""}
                      onChange={(e, newValue) => handleAddressChange(index, "city", newValue)}
                      onInputChange={(e, newValue) => handleAddressChange(index, "city", newValue)}
                      disabled={!isEdit || loading}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="City/Municipality"
                          size="small"
                          error={!!errors[`addresses[${index}].city`]}
                          helperText={errors[`addresses[${index}].city`]}
                        />
                      )}
                    />
                  </Grid>

                  {/* Barangay */}
                  <Grid item xs={12} md={2}>
                    <Autocomplete
                      freeSolo
                      disableClearable
                      options={[
                        ...new Set(
                          nationalAddresses
                            .filter(d => d.city === clientAddress.city)
                            .map(d => d.barangay)
                        )
                      ].sort()}
                      value={clientAddress.barangay || ""}
                      onChange={(e, newValue) => handleAddressChange(index, "barangay", newValue)}
                      onInputChange={(e, newValue) => handleAddressChange(index, "barangay", newValue)}
                      disabled={!isEdit || loading}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Barangay"
                          size="small"
                          error={!!errors[`addresses[${index}].barangay`]}
                          helperText={errors[`addresses[${index}].barangay`]}
                        />
                      )}
                    />
                  </Grid>

                  {/* Zip Code */}
                  <Grid item xs={12} md={1}>
                    <TextField
                      name={`zipcode ${index + 1}`}
                      label="Zip Code"
                      value={clientAddress.zipcode}
                      onChange={e => handleAddressChange(index, "zipcode", e.target.value)}
                      fullWidth
                      size="small"
                      disabled={!isEdit || loading}
                      error={!!errors[`addresses[${index}].zipcode`]}
                      helperText={errors[`addresses[${index}].zipcode`]}
                    />
                  </Grid>

                  {/* Area (sqm) */}
                  <Grid item xs={12} md={1}>
                    <TextField
                      name={`area ${index + 1}`}
                      label="Area (sqm)"
                      value={clientAddress.area}
                      onChange={e => handleAddressChange(index, "area", e.target.value)}
                      fullWidth
                      size="small"
                      disabled={!isEdit || loading}
                      error={!!errors[`addresses[${index}].area`]}
                      helperText={errors[`addresses[${index}].area`]}
                    />
                  </Grid>

                  {/* Billing Address */}
                  <Grid item xs={12} md={2}>
                    <FormControlLabel
                      control={<Checkbox checked={clientAddress.billing} onChange={() => handleAddressCheckboxChange(index)} size="small" disabled={!isEdit || loading}/>} 
                      label="Billing Address"
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        height: '40px',
                        marginTop: 0,
                        '& .MuiTypography-root': {
                          fontSize: '0.875rem'
                        },
                        '& .MuiCheckbox-root': {
                          padding: '4px'
                        }
                      }}
                    />
                  </Grid>

                  {/* Remove Address - positioned on the same row */}
                  <Grid item xs={12} md={10} display="flex" justifyContent="flex-end" alignItems="center">
                    {clientAddresses.length > 1 && (
                      <Button
                        color="error"
                        size="small"
                        onClick={() => handleRemoveAddress(index)}
                        disabled={!isEdit || loading}
                        sx={{ height: '40px' }}
                      >
                        Remove Address
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Grid>
  </>
);

export default ClientAddress;
